import { Building2, ExternalLink, Handshake, Link2, Sparkles } from "lucide-react";
import { CHECK_SHORT, GROUPED_CHECKS, groupItems, slug } from "../lib/hygiene";
import { fmtDate } from "../lib/constants";
import { shortName } from "./OwnerBreakdown";
import { IgnoreButton } from "./IgnoreButton";

const TypeBadge = ({ type }) => (
  <span className={`num shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${type === "deal" ? "bg-brand/10 text-brand" : "bg-slate-100 text-slate-600"}`}>{type}</span>
);

const Row = ({ it, check, indent, canIgnore, onIgnored }) => {
  const keys = check ? [check, ...(it.issues || []).filter((k) => k !== check)] : it.issues || [];
  const detail = check ? it.details?.[check] : Object.values(it.details || {}).join(" · ");
  const showCs = it.cs_deals?.length && (!check || check === "cs_repeat");
  const dup = it.duplicate_of && (!check || check === "company_auto_created") ? it.duplicate_of : null;
  return (
    <div data-testid={`hygiene-item-${it.type}-${it.id}`} className={`py-2 pr-3 text-sm ${indent ? "pl-7 sm:pl-10" : "pl-3 sm:pl-4"}`}>
      <div className="flex items-center gap-2 min-w-0">
        <TypeBadge type={it.type} />
        {it.url ? (
          <a href={it.url} target="_blank" rel="noreferrer" data-testid={`hygiene-item-link-${it.id}`} title="Open in HubSpot" className="font-semibold text-ink truncate hover:text-brand hover:underline inline-flex items-center gap-1 min-w-0">
            <span className="truncate">{it.name}</span> <ExternalLink className="w-3 h-3 shrink-0 text-slate-400" />
          </a>
        ) : (
          <span className="font-semibold text-ink truncate">{it.name}</span>
        )}
        {it.pipeline && it.type === "deal" && (
          <span className={`hidden sm:inline num shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${it.pipeline === "CC Channel" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`}>
            {it.pipeline === "Partnerships" || it.pipeline === "CC Channel" ? <Handshake className="inline w-3 h-3 mr-1 -mt-0.5" /> : null}
            {it.pipeline}
          </span>
        )}
        <span className="ml-auto num text-xs text-slate-400 shrink-0" title="Created in HubSpot">{it.created ? fmtDate(it.created) : "—"}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5 min-w-0">
        <span data-testid={`hygiene-item-owner-${it.id}`} title={`Deal owner: ${it.owner}`} className="rounded-full bg-ink/5 text-slate-600 px-2 py-0.5 text-[11px] font-semibold shrink-0">{shortName(it.owner)}</span>
        {(it.venue || it.company) && it.type === "deal" && (
          <span className="text-xs text-slate-500 truncate inline-flex items-center gap-1 min-w-0">
            <Building2 className="w-3 h-3 shrink-0 text-slate-400" />
            <span className="truncate">{[it.venue, it.company && it.company !== it.venue ? it.company : null].filter(Boolean).join(" · ")}</span>
          </span>
        )}
        <span className="flex flex-wrap items-center gap-1 sm:ml-auto">
          {it.ai?.company_type && (
            <span data-testid={`hygiene-item-ai-type-${it.id}`} title={it.ai.company_type.manual ? "Set by the admin" : `AI suggestion – ${Math.round(it.ai.company_type.confidence * 100)} % sure (HubSpot is not changed)`} className="inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand/5 text-brand px-2 py-0.5 text-[11px] font-semibold">
              <Sparkles className="w-3 h-3" /> AI: {it.ai.company_type.label === "venue" ? "Venue" : "Merchant"} {Math.round(it.ai.company_type.confidence * 100)} %
            </span>
          )}
          {it.ai?.placeholder !== undefined && (it.issues || []).includes("default_name") && (
            <span data-testid={`hygiene-item-ai-placeholder-${it.id}`} title={`AI recognised a placeholder name – ${Math.round(it.ai.placeholder * 100)} % sure`} className="inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand/5 text-brand px-2 py-0.5 text-[11px] font-semibold">
              <Sparkles className="w-3 h-3" /> AI
            </span>
          )}
          {keys.map((k) => (
            <span key={k} data-testid={`hygiene-item-issue-${it.id}-${k}`} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${k === check ? "bg-brand text-white" : "bg-slate-100 text-slate-700"}`}>{CHECK_SHORT[k] || k}</span>
          ))}
          {canIgnore && check && <IgnoreButton check={check} item={it} testId={`hygiene-ignore-${it.id}`} onDone={onIgnored} />}
        </span>
      </div>
      {detail && !showCs && <div className="mt-1 text-xs text-slate-500 break-words" data-testid={`hygiene-item-detail-${it.id}`}>{detail}</div>}
      {showCs ? (
        <div className="mt-1" data-testid={`hygiene-item-cs-deals-${it.id}`}>
          <div className="text-xs text-slate-500">{it.cs_deals.length} CS deals created from this WON deal – keep one, close or merge the rest:</div>
          <ul className="mt-0.5 ml-4 sm:ml-6 border-l-2 border-panel-border pl-3 space-y-0.5">
            {it.cs_deals.map((c) => (
              <li key={c.id} className="text-xs flex items-center gap-2 min-w-0" data-testid={`hygiene-cs-deal-${c.id}`}>
                <a href={c.url} target="_blank" rel="noreferrer" className="font-semibold text-ink truncate hover:text-brand hover:underline inline-flex items-center gap-1 min-w-0" title="Open the CS deal in HubSpot">
                  <span className="truncate">{c.name}</span> <ExternalLink className="w-3 h-3 shrink-0 text-slate-400" />
                </a>
                <span className="num text-slate-400 shrink-0">{c.id}</span>
                <span className="num text-slate-400 shrink-0 ml-auto">{c.created ? fmtDate(c.created) : "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {dup && (
        <div className="mt-1 text-xs text-slate-600 flex items-center gap-1 min-w-0" data-testid={`hygiene-item-duplicate-of-${it.id}`}>
          <span className="shrink-0">Merge into</span>
          <a href={dup.url} target="_blank" rel="noreferrer" className="font-semibold text-ink truncate hover:text-brand hover:underline inline-flex items-center gap-1 min-w-0" title="Open the other record in HubSpot">
            <span className="truncate">{dup.name}</span> <ExternalLink className="w-3 h-3 shrink-0 text-slate-400" />
          </a>
          <span className="num text-slate-400 shrink-0">{dup.id}</span>
          <span className="text-slate-400 shrink-0">· {dup.manual ? "created by hand" : "also auto-created, older"}</span>
        </div>
      )}
      {it.fix && (
        <div className="mt-1 text-xs flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0" data-testid={`hygiene-item-fix-${it.id}`}>
          <span className="shrink-0 text-slate-500">{it.won_at ? `Won ${fmtDate(it.won_at)} · ` : ""}Not on the board – set <b className="text-ink">Company type = Venue</b> on:</span>
          {(it.companies || []).map((c) => (
            <a key={c.id} href={c.url} target="_blank" rel="noreferrer" data-testid={`hygiene-item-fix-company-${c.id}`} title="Opens the company in HubSpot – set Company type = Venue there" className="inline-flex items-center gap-1 rounded-full bg-ink text-page px-2.5 py-0.5 text-[11px] font-semibold hover:bg-ink/85 shrink-0 max-w-full">
              <Building2 className="w-3 h-3 shrink-0" /> <span className="truncate">{c.name}</span> <ExternalLink className="w-3 h-3 shrink-0 opacity-70" />
            </a>
          ))}
        </div>
      )}
      {it.pay_fix && (
        <div className="mt-1 text-xs flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0" data-testid={`hygiene-item-pay-fix-${it.id}`}>
          <span className="shrink-0 text-slate-500">{it.won_at ? `Won ${fmtDate(it.won_at)} · ` : ""}Payments line items: <span className="text-ink">{(it.items || []).join(", ")}</span> – no provider named{it.provider_hint ? <> (deal name says <b className="text-ink">{it.provider_hint}</b>)</> : null}. Add e.g. <b className="text-ink">Card Payments – Adyen (POS)</b> / <b className="text-ink">Card Payments – Viva (Ecom)</b>.</span>
        </div>
      )}
      {it.gap && (
        <div className="mt-1 text-xs flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0" data-testid={`hygiene-item-hint-${it.id}`}>
          {it.hint ? (
            <>
              <span className="shrink-0 text-slate-500">Likely source WON deal:</span>
              <a href={it.hint.url} target="_blank" rel="noreferrer" data-testid={`hygiene-item-hint-link-${it.id}`} className="font-semibold text-ink truncate hover:text-brand hover:underline inline-flex items-center gap-1 min-w-0" title="Open the WON deal in HubSpot">
                <span className="truncate">{it.hint.name}</span> <ExternalLink className="w-3 h-3 shrink-0 text-slate-400" />
              </a>
              <span className="num text-slate-400 shrink-0">{it.hint.id}</span>
              <span className="text-slate-400 shrink-0" data-testid={`hygiene-item-hint-reasons-${it.id}`}>· {it.hint.reasons.join(" · ")}{it.hint.created ? ` · created ${fmtDate(it.hint.created)}` : ""}</span>
              <a href={it.url} target="_blank" rel="noreferrer" data-testid={`hygiene-item-associate-${it.id}`} title="Opens the CS deal in HubSpot – add the WON deal under Associations → Deals" className="sm:ml-auto inline-flex items-center gap-1 rounded-full bg-ink text-page px-2.5 py-0.5 text-[11px] font-semibold hover:bg-ink/85 shrink-0">
                <Link2 className="w-3 h-3" /> Associate in HubSpot
              </a>
            </>
          ) : (
            <span className="text-slate-500">No likely source found – no WON deal on this venue or with a matching name. Check the deal in HubSpot.</span>
          )}
        </div>
      )}
    </div>
  );
};

// Findings list; duplicate / repeated-handoff checks are rendered as merge groups. Admins can mark a finding
// (or a whole group) as intentional – it then disappears until it changes.
export const HygieneList = ({ items, check, limit, onMore, canIgnore = false, onIgnored }) => {
  const grouped = GROUPED_CHECKS.includes(check);
  const shown = items.slice(0, limit);
  if (!items.length) return <div className="p-4 text-sm text-slate-500" data-testid="hygiene-empty">Nothing matches – all clean here.</div>;
  return (
    <>
      {grouped ? (
        groupItems(shown).map((g) => (
          <div key={g.key} data-testid={`hygiene-group-${slug(g.label || g.key)}`} className="border-b border-panel-border last:border-b-0">
            <div className="sticky top-0 z-10 flex items-center gap-2 flex-wrap bg-slate-50/95 backdrop-blur px-3 sm:px-4 py-1.5 text-xs border-b border-panel-border">
              <span className="font-bold text-ink truncate">“{g.label || g.items[0].name}”</span>
              <span className="text-slate-500 shrink-0">· <b className="num text-ink">{g.items.length}</b> {g.items.length === 1 ? "deal" : "deals"} – keep one, merge the rest</span>
              {canIgnore && g.label && <span className="ml-auto"><IgnoreButton check={check} group={g.label} label="Ignore group" testId={`hygiene-ignore-group-${slug(g.label)}`} onDone={onIgnored} /></span>}
            </div>
            <div className="divide-y divide-panel-border">{g.items.map((it) => <Row key={`${it.type}-${it.id}`} it={it} check={check} indent canIgnore={canIgnore} onIgnored={onIgnored} />)}</div>
          </div>
        ))
      ) : (
        <div className="divide-y divide-panel-border">{shown.map((it) => <Row key={`${it.type}-${it.id}`} it={it} check={check} canIgnore={canIgnore} onIgnored={onIgnored} />)}</div>
      )}
      {items.length > limit && (
        <button type="button" data-testid="hygiene-show-more" onClick={onMore} className="w-full py-2.5 text-xs font-semibold text-brand hover:bg-brand/5 transition-colors">
          Show {Math.min(200, items.length - limit)} more of {items.length - limit} remaining
        </button>
      )}
    </>
  );
};
